// LocationAutocomplete.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateLocationCoords } from "../store/slices/searchSlice";

const LocationAutocomplete = ({
  onLocationSelect,
  isGoogleLoaded,
  value = '',
  onChange,
  deleted = false
}) => {
  const { loc } = useSelector((state) => state.search);

  const [locationQuery, setLocationQuery] = useState(loc?.location || value);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setLocationQuery(value);
  }, [value]);

  useEffect(() => {
    if (isGoogleLoaded && window.google && window.google.maps && window.google.maps.places && locationQuery?.trim()) {
      const autocomplete = new window.google.maps.places.AutocompleteService();
      const options = {
        input: locationQuery,
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'FR' }
      };

      autocomplete.getPlacePredictions(options, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      });
    } else {
      setSuggestions([]);
    }
  }, [locationQuery, isGoogleLoaded]);

  const handleSelect = async (place) => {
    setLocationQuery(place.description);
    setShowDropdown(false);

    // Fetch the latitude and longitude using the Geocoder API
    if (isGoogleLoaded && window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: place.description }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK) {
          const { lat, lng } = results[0].geometry.location;
          const cityName = results[0].address_components.find(component =>
            component.types.includes('locality')
          )?.long_name;

          // Dispatch the update to Redux with the new coordinates and cityName
          dispatch(updateLocationCoords({
            lat: lat(),
            lng: lng(),
            location: place.description,
            cityName
          }));
        }
      });
    }

    if (onLocationSelect) {
      onLocationSelect(place);
    }

    if (onChange) {
      onChange(place.description);
    }
  };

  const handleGeolocate = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (isGoogleLoaded && window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            const latLng = new window.google.maps.LatLng(position.coords.latitude, position.coords.longitude);

            geocoder.geocode({ location: latLng }, (results, status) => {
              if (status === "OK" && results[0]) {
                const address = results[0].formatted_address;
                const cityName = results[0].address_components.find(component =>
                  component.types.includes('locality')
                )?.long_name;

                setLocationQuery(address);
                setShowDropdown(false);

                // Dispatch the location and coordinates update
                dispatch(updateLocationCoords({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  location: address,
                  cityName
                }));

                if (onLocationSelect) {
                  onLocationSelect({ description: address, place_id: results[0].place_id });
                }
                if (onChange) {
                  onChange(address);
                }
              } else {
                // console.error("Geocoding error:", status);
                alert("Unable to retrieve your address. Please try again.");
              }
            });
          }
        },
        (error) => {
          // console.error("Geolocation error:", error);
          alert("Impossible d'accéder à votre position. Veuillez activer la géolocalisation.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocationQuery(newValue);
    if (onChange) {
      onChange(newValue);
    }
    if (!newValue) {
      if (onLocationSelect) {
        onLocationSelect(null);
      }
    }
  };

  const handleClear = () => {
    setLocationQuery("");
    setShowDropdown(false);
    dispatch(updateLocationCoords({ lat: null, lng: null }));
    if (onChange) {
      onChange("");
    }
  };

  return (
    <div className="dropdown-container">
      <div className="d-flex justify-content-between">
        <input
          id="location-query"
          type="text"
          className=" custom-input body grey500 w-100 search-input"
          placeholder="Ville, adresse ..."
          value={locationQuery}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          autoComplete="off"
        />
        {locationQuery && deleted && (
          <button type="button" className="link-blue" onClick={handleClear}>
            <img src="/images/x-03.svg" alt="x icon" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="dropdown-menu show" style={{ maxHeight: "300px", overflowY: "auto" }}>
          {locationQuery?.trim() === "" ? (
            <div
              
              className="dropdown-item pointer"
              onClick={handleGeolocate}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
              </svg>
              Me géolocaliser
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                // href="#!"
                className="dropdown-item body grey500 pointer"
                onClick={() => handleSelect(suggestion)}
              >
                {suggestion.description}
              </div>
            ))
          ) : (
            ''
          )}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
